import React, { useState } from 'react';
import { 
  Typography, 
  Container, 
  Box, 
  Button, 
  Stack,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ComponentDemo from '../components/ComponentDemo/ComponentDemo';
import AutobiaConfirmationModal from '../components/Modals/AutobiaConfirmationModal';

const Modals = () => {
  const [modalStates, setModalStates] = useState({
    basic: false,
    success: false,
    error: false,
    warning: false,
    minimal: false,
    elevated: false,
    withContent: false,
  });

  const [customMessage, setCustomMessage] = useState('This is a custom confirmation message that explains the action to be performed.');
  const [loading, setLoading] = useState(false);

  const openModal = (type) => {
    setModalStates(prev => ({ ...prev, [type]: true }));
  };

  const closeModal = (type) => {
    setModalStates(prev => ({ ...prev, [type]: false }));
  };

  const handleConfirm = (type) => {
    console.log(`${type} confirmed`);
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      closeModal(type);
    }, 2000);
  };

  const basicModalUsage = `import AutobiaConfirmationModal from '../components/Modals/AutobiaConfirmationModal';

function MyComponent() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        Open Modal
      </Button>
      <AutobiaConfirmationModal
        open={open}
        onClose={() => setOpen(false)}
        title="Confirm Action"
        message="Are you sure you want to proceed?"
        onConfirm={() => console.log('Confirmed!')}
      />
    </>
  );
}`;

  const advancedModalUsage = `<AutobiaConfirmationModal
  open={open}
  onClose={handleClose}
  title="Delete Account"
  message="This action cannot be undone. All your data will be permanently deleted."
  type="error"
  variant="elevated"
  confirmText="Delete Forever"
  cancelText="Keep Account"
  onConfirm={handleDelete}
  loading={isDeleting}
  slideDirection="up"
>
  <Stack spacing={2}>
    <TextField
      placeholder="Type 'DELETE' to confirm"
      fullWidth
    />
    <Typography variant="caption" color="text.secondary">
      This is an irreversible action. Please make sure you have backed up your data.
    </Typography>
  </Stack>
</AutobiaConfirmationModal>`;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Modals
      </Typography>
      
      <Typography variant="body1" paragraph color="text.secondary">
        Interactive modal dialogs for confirmations, alerts, and complex user interactions. 
        Built with smooth animations and multiple variants.
      </Typography>

      {/* Basic Confirmation Modal */}
      <ComponentDemo
        title="Basic Confirmation Modal"
        description="Simple confirmation modal with customizable title and message."
        component={
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button 
              variant="contained" 
              onClick={() => openModal('basic')}
            >
              Open Basic Modal
            </Button>
            <Button 
              variant="contained" 
              color="success"
              onClick={() => openModal('success')}
            >
              Success Modal
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={() => openModal('error')}
            >
              Error Modal
            </Button>
            <Button 
              variant="contained" 
              color="warning"
              onClick={() => openModal('warning')}
            >
              Warning Modal
            </Button>
          </Stack>
        }
        usage={basicModalUsage}
      />

      {/* Modal Variants */}
      <ComponentDemo
        title="Modal Variants"
        description="Different visual styles and layouts for various use cases."
        component={
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button 
              variant="outlined"
              onClick={() => openModal('minimal')}
            >
              Minimal Variant
            </Button>
            <Button 
              variant="outlined"
              onClick={() => openModal('elevated')}
            >
              Elevated Variant
            </Button>
            <Button 
              variant="outlined"
              onClick={() => openModal('withContent')}
            >
              With Custom Content
            </Button>
          </Stack>
        }
        usage={advancedModalUsage}
      />

      {/* Interactive Demo */}
      <ComponentDemo
        title="Interactive Demo"
        description="Test different configurations and see how the modal behaves."
        component={
          <Stack spacing={3}>
            <TextField
              label="Custom Message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch 
                  checked={loading}
                  onChange={(e) => setLoading(e.target.checked)}
                />
              }
              label="Loading State"
            />
            <Button 
              variant="contained"
              onClick={() => openModal('basic')}
            >
              Test Custom Modal
            </Button>
          </Stack>
        }
      />

      {/* All Modal Instances */}
      <AutobiaConfirmationModal
        open={modalStates.basic}
        onClose={() => closeModal('basic')}
        title="Confirm Action"
        message={customMessage}
        onConfirm={() => handleConfirm('basic')}
        loading={loading}
      />

      <AutobiaConfirmationModal
        open={modalStates.success}
        onClose={() => closeModal('success')}
        title="Success"
        message="Your operation completed successfully!"
        type="success"
        confirmText="Great!"
        onConfirm={() => closeModal('success')}
      />

      <AutobiaConfirmationModal
        open={modalStates.error}
        onClose={() => closeModal('error')}
        title="Delete Item"
        message="This action cannot be undone. Are you sure?"
        type="error"
        confirmText="Delete"
        onConfirm={() => closeModal('error')}
      />

      <AutobiaConfirmationModal
        open={modalStates.warning}
        onClose={() => closeModal('warning')}
        title="Warning"
        message="This action may have unintended consequences."
        type="warning"
        confirmText="Proceed"
        onConfirm={() => closeModal('warning')}
      />

      <AutobiaConfirmationModal
        open={modalStates.minimal}
        onClose={() => closeModal('minimal')}
        title="Minimal Style"
        message="Clean and simple modal design."
        variant="minimal"
        onConfirm={() => closeModal('minimal')}
      />

      <AutobiaConfirmationModal
        open={modalStates.elevated}
        onClose={() => closeModal('elevated')}
        title="Elevated Style"
        message="Enhanced visual design with gradients."
        variant="elevated"
        type="success"
        onConfirm={() => closeModal('elevated')}
      />

      <AutobiaConfirmationModal
        open={modalStates.withContent}
        onClose={() => closeModal('withContent')}
        title="Transfer Funds"
        message="Please review the transfer details before confirming."
        type="warning"
        onConfirm={() => closeModal('withContent')}
      >
        <Stack spacing={2}>
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2">Transfer Details:</Typography>
            <Typography variant="body2">Amount: $1,250.00</Typography>
            <Typography variant="body2">From: Business Account</Typography>
            <Typography variant="body2">To: Savings Account</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Transfer will be processed immediately and cannot be reversed.
          </Typography>
        </Stack>
      </AutobiaConfirmationModal>
    </Container>
  );
};

export default Modals;